import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useAppearance } from '@traceio-org/auth-ui';
import { NavigationShell } from '@traceio-org/ui';
import { useAbilities, useAdmin } from '../context';
import {
  KeysSummaryProvider,
  keysAttentionWords,
  keysNeedingAttention,
  useKeysSummary,
} from '../keysSummary';
import { useTicketInboxCount } from '../ticketsInbox';
import { ADMIN_PRODUCT_NAME, adminNavigation, sectionForPath, sectionIcon } from '../navigation';
import { ACCOUNT_BILLING_ID, APPEARANCE_ITEM_ID, SIGN_OUT_ID, useAccountMenu } from './AccountMenu';
import { AppearanceMenu } from './AppearanceMenu';

/**
 * The staff shell: the library's NavigationShell with Trace Admin's identity and the navigation this staff
 * member's permissions show (Billing only with staff.billing.read), the current area as the top bar title, and the
 * staff member's account menu in the sidebar footer (AccountMenu), with Keys' count of keys expired or due
 * (KeysSummaryProvider) as a red badge. Navigation clicks go through the router (no page reload, so the confirmed
 * session and access stay in memory); modified clicks keep the browser's behavior, as the shell implements. The
 * account menu's Appearance entry opens the appearance menu, and its Account & billing and Sign out entries go to
 * the account hub.
 */
export function AdminLayout() {
  return (
    <KeysSummaryProvider>
      <AdminShell />
    </KeysSummaryProvider>
  );
}

/**
 * The Keys link's accessible name and tooltip in the words of its badge ("Keys, 2 keys expired or due"). Risky, so
 * spelled out: trace-ui's NavigationShell (vendored, e5ce5602ae) names a badge "Keys, 2 items" and has no prop for the
 * words, so this sets aria-label and title on its Keys link after each render of the navigation (the rail and the
 * expanded list both), and only when they differ, so the observer never loops. Remove once trace-ui takes a badge label.
 */
function useKeysBadgeWords(count: number): void {
  useEffect(() => {
    if (count <= 0 || typeof MutationObserver === 'undefined') return;
    const label = `Keys, ${keysAttentionWords(count)}`;
    const apply = () => {
      for (const link of document.querySelectorAll<HTMLElement>('.trace-nav-link[href="/keys"]')) {
        if (link.getAttribute('aria-label') !== label) link.setAttribute('aria-label', label);
        if (link.getAttribute('title') !== label) link.setAttribute('title', label);
      }
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-label', 'title'],
    });
    return () => observer.disconnect();
  }, [count]);
}

function AdminShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const abilities = useAbilities();
  const keysAttention = keysNeedingAttention(useKeysSummary().summary);
  useKeysBadgeWords(keysAttention);
  const inbox = useTicketInboxCount(abilities.readTickets);
  const groups = useMemo(
    () => adminNavigation(abilities, { keys: keysAttention, tickets: inbox }),
    [abilities, keysAttention, inbox],
  );
  const section = sectionForPath(location.pathname);
  const { hubOrigin, assign } = useAdmin();
  // Shared with the account hub through the cookie on the parent domain (auth-ui appearance).
  const { preference, shared, setPreference } = useAppearance(hubOrigin);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const closeAppearance = useCallback(() => setAppearanceOpen(false), []);
  const account = useAccountMenu(preference);
  return (
    <>
      <NavigationShell
        {...account}
        groups={groups}
        activeId={section?.id ?? ''}
        title={section?.label ?? ADMIN_PRODUCT_NAME}
        {...(section ? { titleIcon: sectionIcon(section) } : {})}
        onNavigate={(item) => {
          if (item.id === APPEARANCE_ITEM_ID) setAppearanceOpen(true);
          else if (item.id === ACCOUNT_BILLING_ID || item.id === SIGN_OUT_ID) assign(item.href);
          else navigate(item.href);
        }}
        searchLabel="Search Trace Admin pages"
        persistenceKey="trace-admin.navigation.collapsed"
      >
        <Outlet />
      </NavigationShell>
      {appearanceOpen ? (
        <AppearanceMenu
          preference={preference}
          shared={shared}
          onChange={setPreference}
          onClose={closeAppearance}
        />
      ) : null}
    </>
  );
}
